import {
  AbilityId,
  Actor,
  Body,
  Bracing,
  Buff,
  Casting,
  Collider,
  Dashing,
  Explosive,
  Falling,
  Hitstun,
  Instability,
  Kit,
  Launch,
  PlayerTag,
  Projectile,
  Renderable,
  Shockwave,
  Static,
  Stunned,
  Transform,
  Velocity,
  VortexField,
  type PlayerId,
} from '@/components'
import type { EventBus } from '@/core/EventBus'
import type { Entity } from '@/core/ecs/Entity'
import type { World } from '@/core/ecs/World'
import { ArenaResource } from '@/game/arenas/Arena'
import { Events, type HitKind } from '@/game/events'
import { EventsResource, MatchResource, PLAYER_COLORS, worldScale } from '@/game/resources'
import { SceneBridgeResource } from '@/gfx/SceneBridge'
import {
  BOLT,
  BRACE,
  DASH,
  LAUNCH_LIFT,
  VORTEX,
  WAVE,
  launchProfile,
  launchSpeed,
} from './definitions'

/** Launch speed below which a hit is a nudge and takes no control away. */
const LAUNCH_THRESHOLD = 9

/**
 * The single choke point through which every impulse in the game passes.
 *
 * Centralising it means instability scaling, brace absorption, dash armour and
 * hit reporting can never disagree between a push, an explosion and a hazard —
 * a bug class that plagues games where each ability applies its own knockback.
 */
/**
 * Optional layers of a hit, on top of the impulse itself.
 *
 * Displacement, loss of control and the wear that scales future hits are three
 * different things, and every game that solved this treats them separately.
 * Smash's "windbox" is literally an ordinary hitbox with a `Flinchless` flag:
 * the same knockback formula, but no hitstun, no freeze frames and no defensive
 * reaction — see docs/DISPLACEMENT.md §1. Until this existed, the only way to
 * make one fighter differ from another was a multiplier on the same verb, which
 * is exactly what the player called "у всех одинаковые способности".
 */
export interface HitLayers {
  /**
   * Pushes without taking control away. A shoved fighter keeps walking, casting
   * and defending; they just end up somewhere else.
   *
   * The counterweight comes with it, from the same research: a *held* stream of
   * flinchless push is a broken mechanic (Smash's Gust Bellows keeps a victim
   * off the edge indefinitely). Anything using this must be an impulse on a
   * cooldown, and Brace must answer it.
   */
  flinchless?: boolean
  /**
   * Multiplier on how long control is lost, 1 by default. Overwatch keeps this
   * between 0.45 s and 0.75 s and shortens anything longer with the note that it
   * "takes the player out of the fight too long".
   */
  stunScale?: number
}

export function applyKnockback(
  world: World,
  target: Entity,
  dirX: number,
  dirZ: number,
  power: number,
  instabilityAdd: number,
  kind: HitKind,
  source: Entity,
  hitX: number,
  hitZ: number,
  layers: HitLayers = {},
): void {
  if (!world.isAlive(target)) return
  if (world.has(target, Static)) return
  if (world.has(target, Falling)) return
  // Teammates are not targets. Checked here rather than in each ability so no
  // impulse in the game can ever reach an ally: the whole point of a team mode
  // is that your partner is not a hazard you have to play around.
  if (sameTeam(world, source, target)) return

  const vel = world.get(target, Velocity)
  if (!vel) return

  const events = world.resource(EventsResource)
  const instability = world.get(target, Instability)

  // A pickup shield is absolute: nothing moves you and nothing rattles you.
  // Its whole point is a window where you can walk into anything and win the
  // ground, so partial resistance would defeat it.
  const buff = world.get(target, Buff)
  if (buff && buff.shield > 0) {
    // Immune to the impulse, but not to the wear: instability still builds, so
    // the shield postpones the consequences rather than erasing them.
    if (instability && instabilityAdd > 0) {
      instability.value = Math.min(instability.max, instability.value + instabilityAdd)
      instability.sinceHit = 0
    }
    events.emit(Events.Hit, {
      source,
      target,
      x: hitX,
      z: hitZ,
      dirX,
      dirZ,
      power: 0,
      instability: instabilityAdd,
      kind,
      braced: true,
      launched: false,
      lethal: false,
    })
    return
  }

  // `power` already carries the instability scaling: callers compute it from
  // the ability's base/scaling split (`launchSpeed`), so this function must not
  // apply it a second time. See docs/PHYSICS.md §6.2.
  let effective = power
  let braced = false

  if (world.has(target, Bracing)) {
    // What breaks a stance is a design decision, not an arithmetic accident:
    // a charging body-check, an anomaly and an explosion go through it, while
    // Push and Wave — the two things Brace exists to answer — do not, however
    // battered the target already is.
    const heavy = kind === 'dash' || kind === 'vortex' || kind === 'explosion'
    // `effective` already carries the instability multiplier, so a rattled
    // fighter's stance breaks under blows a fresh one would have absorbed.
    if (heavy || effective >= BRACE.breakThreshold) {
      world.remove(target, Bracing)
      world.add(target, Stunned, { timeLeft: BRACE.breakStun })
      effective *= 0.6
    } else {
      effective *= BRACE.knockbackMul * (world.get(target, Kit)?.braceMul ?? 1)
      braced = true
    }
  }

  if (world.has(target, Dashing)) effective *= DASH.knockbackResist

  // Props take impulse in inverse proportion to their mass.
  const body = world.get(target, Body)
  if (body && !instability) effective *= body.invMass

  // Actors are launched; props just take the impulse and slide.
  //
  // A launch replaces the velocity rather than adding to it, because a launch
  // is a *state*, not a nudge: full speed for a moment, then a hard brake to a
  // stop, with control returned exactly when the slide ends. Adding to whatever
  // the victim was already doing is what made two identical hits produce
  // wildly different outcomes. See docs/PHYSICS.md §6.2.
  let launched = false
  let lethal = false

  // A stance stops a shove dead. Smash does the same — a push that deals no
  // damage simply does not work against a shield — and it is what keeps a
  // flinchless move from being the broken thing it is in that game: the victim
  // cannot be walked off the edge, because they have an answer that costs them
  // a button rather than nothing.
  if (layers.flinchless && world.has(target, Bracing)) {
    events.emit(Events.Hit, {
      source,
      target,
      x: hitX,
      z: hitZ,
      dirX,
      dirZ,
      power: 0,
      instability: 0,
      kind,
      braced: true,
      launched: false,
      lethal: false,
    })
    return
  }

  // A flinchless hit moves you and nothing else: it never opens the launch
  // state, so the victim keeps their feet and their options. That is the whole
  // difference between a shove and a throw.
  if (layers.flinchless) {
    vel.x += dirX * effective
    vel.z += dirZ * effective
    if (instability && instabilityAdd > 0) {
      instability.value = Math.min(instability.max, instability.value + instabilityAdd)
      instability.sinceHit = 0
    }
    events.emit(Events.Hit, {
      source,
      target,
      x: hitX,
      z: hitZ,
      dirX,
      dirZ,
      power: effective,
      instability: instabilityAdd,
      kind,
      braced,
      launched: false,
      lethal: false,
    })
    return
  }

  if (instability && effective >= LAUNCH_THRESHOLD) {
    const profile = launchProfile(effective)
    const existing = world.get(target, Launch)
    // A stronger launch overrides a weaker one already in flight.
    if (!existing || effective > existing.speed) {
      launched = true
      lethal = endsOffPlatform(world, target, dirX, dirZ, profile.distance)
      vel.x = dirX * effective
      vel.z = dirZ * effective
      // The lift that turns a slide into a skipping arc.
      vel.y = effective * LAUNCH_LIFT
      world.add(target, Launch, {
        airborne: true,
        bounces: 0,
        duration: profile.duration,
        elapsed: 0,
        speed: effective,
        di: 0,
      })
      // Control returns exactly when the fighter stops moving: no dead time
      // standing still, and no steering out of a hit mid-flight. A move may
      // shorten that window without changing how far the hit carries.
      const stun = profile.duration * (layers.stunScale ?? 1)
      world.add(target, Hitstun, { timeLeft: stun, duration: stun })
    }
  } else {
    vel.x += dirX * effective
    vel.z += dirZ * effective
  }

  if (instability && instabilityAdd > 0) {
    instability.value = Math.min(instability.max, instability.value + instabilityAdd)
    instability.sinceHit = 0
  }

  events.emit(Events.Hit, {
    source,
    target,
    x: hitX,
    z: hitZ,
    dirX,
    dirZ,
    power: effective,
    instability: instabilityAdd,
    kind,
    braced,
    launched,
    lethal,
  })

  // Barrels detonate when hit hard enough, chaining into other barrels.
  const explosive = world.get(target, Explosive)
  if (explosive && explosive.armed && effective >= explosive.threshold) {
    explosive.armed = false
    explosive.fuse = 0.08
  }
}

/**
 * True when two entities are fighters on the same team.
 *
 * Self-hits are excluded on purpose: a vortex still drags its own caster, and a
 * barrel someone shot still throws them. Those are costs the player chose, not
 * friendly fire.
 */
function sameTeam(world: World, source: Entity, target: Entity): boolean {
  if (source === target) return false
  const match = world.tryResource(MatchResource)
  if (!match) return false
  const a = world.get(source, PlayerTag)
  const b = world.get(target, PlayerTag)
  if (!a || !b) return false
  return (match.teams[a.player] ?? a.player) === (match.teams[b.player] ?? b.player)
}

/**
 * Whether a launch of this length, along this direction, ends over the void.
 *
 * Sampled rather than solved: the footprint is an SDF that can have holes, so
 * the endpoint alone would call a launch across a gap safe. Eight samples is
 * enough to catch every gap wider than a fighter.
 */
function endsOffPlatform(
  world: World,
  target: Entity,
  dirX: number,
  dirZ: number,
  distance: number,
): boolean {
  const arena = world.tryResource(ArenaResource)
  if (!arena) return false
  const tr = world.get(target, Transform)
  if (!tr) return false
  const steps = 8
  for (let i = steps; i >= steps - 2; i--) {
    const d = (distance * i) / steps
    if (arena.contains(tr.x + dirX * d, tr.z + dirZ * d)) return false
  }
  return true
}

/** Every entity a force can act upon. */
function hittables(world: World): readonly Entity[] {
  return world.query([Transform, Velocity, Collider], { none: [Static] }).entities()
}

// ────────────────────────────────────────────────────────────────── ТОЛЧОК ──

/**
 * Throws the plasma bolt. Everything it does afterwards — flight, bounces,
 * ricochets, the hit itself — belongs to `ProjectileSystem`.
 */
export function firePush(world: World, caster: Entity): void {
  const tr = world.read(caster, Transform)
  const tag = world.get(caster, PlayerTag)
  const fx = Math.sin(tr.angle)
  const fz = Math.cos(tr.angle)

  const gfx = world.tryResource(SceneBridgeResource)
  const e = world.createEntity()
  // Clear of the caster's own collider: the crescent is 0.85 m across and a
  // fighter 0.75 m, so a closer spawn puts it inside the person throwing it.
  const x = tr.x + fx * 1.5
  const z = tr.z + fz * 1.5

  world.add(e, Transform, {
    x,
    z,
    y: BOLT.spawnY,
    prevX: x,
    prevZ: z,
    prevY: BOLT.spawnY,
    angle: tr.angle,
  })
  const boltSpeed = BOLT.speed * (world.get(caster, Kit)?.boltSpeed ?? 1)
  world.add(e, Velocity, { x: fx * boltSpeed, z: fz * boltSpeed, y: BOLT.lift })
  world.add(e, Projectile, {
    owner: caster,
    player: (tag?.player ?? 0) as PlayerId,
    radius: BOLT.radius,
    halfWidth: BOLT.halfWidth,
    selfGrace: BOLT.selfGrace,
    life: BOLT.life,
    bounces: 0,
  })
  if (gfx) {
    world.add(e, Renderable, {
      objectId: gfx.addBolt(tag?.color ?? PLAYER_COLORS[0]),
    })
  }
}

// ─────────────────────────────────────────────────────────────────── РЫВОК ──

export function startDash(world: World, caster: Entity): void {
  const tr = world.read(caster, Transform)
  const actor = world.read(caster, Actor)
  // Dash in the input direction when there is one, otherwise straight ahead.
  let dx = actor.thrustX
  let dz = actor.thrustZ
  if (Math.hypot(dx, dz) < 0.2) {
    dx = Math.sin(tr.angle)
    dz = Math.cos(tr.angle)
  } else {
    const len = Math.hypot(dx, dz)
    dx /= len
    dz /= len
  }
  tr.angle = Math.atan2(dx, dz)
  world.add(caster, Dashing, {
    timeLeft: DASH.duration,
    dirX: dx,
    dirZ: dz,
    speed: DASH.speed,
    hits: [],
  })
}

/** Body-check resolution, called every fixed step while a dash is live. */
export function resolveDashContacts(world: World, dasher: Entity): void {
  const dash = world.read(dasher, Dashing)
  const tr = world.read(dasher, Transform)
  const myCol = world.read(dasher, Collider)

  for (const target of hittables(world)) {
    if (target === dasher || dash.hits.includes(target)) continue
    const t = world.read(target, Transform)
    const col = world.read(target, Collider)
    const dx = t.x - tr.x
    const dz = t.z - tr.z
    const dist = Math.hypot(dx, dz)
    if (dist > myCol.radius + col.radius + 0.25 || dist < 1e-4) continue

    dash.hits.push(target)
    const isActor = world.has(target, Actor)
    const instability = world.get(target, Instability)?.value ?? 0
    applyKnockback(
      world,
      target,
      dx / dist,
      dz / dist,
      (isActor ? launchSpeed(DASH.launch, instability, worldScale(world)) : DASH.launch.base) *
        (world.get(dasher, Kit)?.dashPower ?? 1),
      isActor ? DASH.instability : 0,
      'dash',
      dasher,
      (tr.x + t.x) / 2,
      (tr.z + t.z) / 2,
    )
  }
}

// ─────────────────────────────────────────────────────────────────── ВОЛНА ──

export function fireWave(world: World, caster: Entity): void {
  const tr = world.read(caster, Transform)
  const tag = world.get(caster, PlayerTag)
  const kit = world.get(caster, Kit)?.wavePower ?? 1
  spawnShockwave(world, {
    owner: caster,
    x: tr.x,
    z: tr.z,
    maxRadius: WAVE.maxRadius * (world.get(caster, Kit)?.waveRadius ?? 1),
    speed: WAVE.expandSpeed,
    // Stored as the base values; the field system scales each victim's launch
    // by their own instability when the ring reaches them.
    power: WAVE.launch.base * kit,
    powerEdge: WAVE.launchEdge.base * kit,
    instability: WAVE.instability,
    color: tag?.color ?? 0x8fff3c,
  })
}

export interface ShockwaveSpec {
  owner: Entity
  x: number
  z: number
  maxRadius: number
  speed: number
  power: number
  powerEdge: number
  instability: number
  color: number
}

export function spawnShockwave(world: World, spec: ShockwaveSpec): Entity {
  const gfx = world.tryResource(SceneBridgeResource)
  const e = world.createEntity()
  world.add(e, Shockwave, {
    owner: spec.owner,
    x: spec.x,
    z: spec.z,
    radius: 0,
    maxRadius: spec.maxRadius,
    speed: spec.speed,
    power: spec.power,
    powerEdge: spec.powerEdge,
    instability: spec.instability,
    hits: [],
  })
  world.add(e, Transform, { x: spec.x, z: spec.z, prevX: spec.x, prevZ: spec.z })
  if (gfx) {
    const id = gfx.addShockwave(spec.color)
    world.add(e, Renderable, { objectId: id })
  }
  return e
}

// ───────────────────────────────────────────────────────────────── ВОРОНКА ──

export function fireVortex(world: World, caster: Entity): void {
  const tr = world.read(caster, Transform)
  const tag = world.get(caster, PlayerTag)

  // Right where the caster stands: the anomaly is a trap you set around
  // yourself, not a projectile whose landing point has to be guessed.
  const x = tr.x
  const z = tr.z

  const gfx = world.tryResource(SceneBridgeResource)
  const e = world.createEntity()
  world.add(e, Transform, { x, z, prevX: x, prevZ: z })
  world.add(e, VortexField, {
    owner: caster,
    player: (tag?.player ?? 0) as PlayerId,
    phase: 'arming',
    timer: VORTEX.arming,
    radius: VORTEX.radius * (world.get(caster, Kit)?.vortexRadius ?? 1),
    pull: VORTEX.pull,
    burstPower: VORTEX.launch.base * (world.get(caster, Kit)?.vortexPower ?? 1),
    instability: VORTEX.instability,
  })
  if (gfx) {
    const id = gfx.addVortexField(
      VORTEX.radius * (world.get(caster, Kit)?.vortexRadius ?? 1),
      tag?.color ?? PLAYER_COLORS[0],
    )
    world.add(e, Renderable, { objectId: id })
  }
}

// ─────────────────────────────────────────────────────────────────── ВЗРЫВ ──

export function explode(
  world: World,
  x: number,
  z: number,
  radius: number,
  power: number,
  instability: number,
  kind: 'barrel' | 'vortex',
  source: Entity,
): void {
  const events: EventBus = world.resource(EventsResource)

  for (const target of hittables(world)) {
    // The vortex drags its caster in — that is its cost — but it must not also
    // blow them up. Planted at your own feet, a burst that included you made
    // the ability strictly self-defeating: you always ate the full impulse.
    if (kind === 'vortex' && target === source) continue

    const t = world.read(target, Transform)
    const dx = t.x - x
    const dz = t.z - z
    const dist = Math.hypot(dx, dz)
    if (dist > radius) continue

    // Linear falloff, with a floor so a point-blank hit is not a divide by zero.
    const falloff = 1 - dist / radius
    const dirX = dist < 1e-4 ? 0 : dx / dist
    const dirZ = dist < 1e-4 ? 1 : dz / dist
    const isActor = world.has(target, Actor)
    const victimInstability = isActor
      ? (world.get(target, Instability)?.value ?? 0)
      : 0
    const scaling = kind === 'vortex' ? VORTEX.launch.scaling : 10
    applyKnockback(
      world,
      target,
      dirX,
      dirZ,
      (power + scaling * (victimInstability / 100)) * (0.45 + 0.55 * falloff),
      isActor ? instability * falloff : 0,
      // A vortex burst reports itself as a vortex, not as a generic explosion.
      // Filed under the same name as a barrel it was invisible in every
      // attribution: an ability nobody could see the results of.
      kind === 'vortex' ? 'vortex' : 'explosion',
      source,
      t.x,
      t.z,
    )
  }

  events.emit(Events.Explosion, { x, z, radius, power, kind })
  // Shake strength is normalised against the same range as every other
  // feedback channel (docs/PHYSICS.md §6.4); the camera squares it.
  events.emit(Events.Shake, { strength: Math.min(1.2, power / 40), x, z })
}

/** True while the ability may not be started (mid-cast, stunned, falling). */
export function isBusy(world: World, e: Entity): boolean {
  return (
    world.has(e, Casting) ||
    world.has(e, Stunned) ||
    world.has(e, Falling) ||
    world.has(e, Dashing)
  )
}

export const ABILITY_FIRE: Record<number, (world: World, caster: Entity) => void> = {
  [AbilityId.Push]: firePush,
  [AbilityId.Dash]: startDash,
  [AbilityId.Wave]: fireWave,
  [AbilityId.Vortex]: fireVortex,
  [AbilityId.Brace]: () => undefined,
}
